import React, { useState } from 'react';
import AttachmentsList from '@app/pages/attachment/AttachmentsList.tsx';
import UploadPanelVisual from '@components/framework/uploadpanel/UploadPanelVisual.tsx';
import UploadAttachmentDialog, { UploadedAttachment } from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';

interface ComponentProps {
  attachments: UploadedAttachment[];
  onChange: (attachments: UploadedAttachment[]) => void;
  disabled?: boolean;
  hideUpload?: boolean;
  loading?: boolean;
}

/**
 * Documents carried over from the source entity plus anything uploaded here.
 *
 * Memoised so edits elsewhere on the form skip re-rendering the list; this only holds when the caller
 * passes a stable `onChange` (create expedition does). `setIn` keeps the attachments array's identity
 * until an attachment is actually added, removed or edited.
 */
const AttachmentsSection: React.FC<ComponentProps> = React.memo(({ attachments, onChange, disabled = false, hideUpload = false, loading = false }) => {
  const [visible, setVisible] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>(null);

  const close = () => {
    setVisible(false);
    setPendingFiles(null);
  };

  return (
    <>
      <AttachmentsList
        attachments={attachments}
        disabled={disabled}
        onDeletedAttachment={attachmentId => onChange((attachments || []).filter(t => t.attachmentId !== attachmentId))}
        onLabelsChange={(attachmentId, labels) => onChange((attachments || []).map(t => (t.attachmentId === attachmentId ? { ...t, labels } : t)))}
      />
      {!hideUpload && (
        <div className={'py-2'}>
          <UploadPanelVisual
            loading={loading}
            onFiles={files => {
              setPendingFiles(files);
              setVisible(true);
            }}
            // The sidebar has its own panel; only one of the two may claim a paste.
            enablePaste={!visible}
            onClick={() => setVisible(true)}
          />
        </div>
      )}
      <UploadAttachmentDialog
        visible={visible}
        onHide={close}
        pendingFiles={pendingFiles}
        onCompleted={documents => {
          onChange([...(attachments || []), ...documents]);
          close();
        }}
      />
    </>
  );
});

AttachmentsSection.displayName = 'AttachmentsSection';

export default AttachmentsSection;
